<script setup lang="ts">
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import FloatLabel from 'primevue/floatlabel'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
// import Skeleton from 'primevue/skeleton'
import type { ComputedPool } from '@/composable/usePoolsData'
import { getDexUrl, toK } from '@/utils'
import { computed } from 'vue'
import { useDexPoolSwap } from '@/composable/useDexPoolSwap'
import { useMyToast } from '@/composable/useMyToast'

const props = defineProps<{
  buyPool: ComputedPool
  sellPool: ComputedPool
  spread: number | null
  chain: string
  amount: number
  slippage: number
}>()

const emit = defineEmits<{
  (e: 'arbitrage-executed', payload: { buyTxHash: string; sellTxHash: string }): void
}>()

const { loading, executeArbitrage } = useDexPoolSwap()
const { addToast } = useMyToast()

const shortAddress = (addr: string) => {
  if (addr.length <= 12) return addr
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

const formatPrice = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—'
  if (v < 0.0001) return v.toExponential(4)
  if (v < 1) return v.toFixed(6)
  if (v < 100) return v.toFixed(4)
  return v.toFixed(2)
}

const hasLiveBuy = computed(() => props.buyPool.ts !== null)
const hasLiveSell = computed(() => props.sellPool.ts !== null)

const spreadSeverity = computed(() => {
  if (props.spread === null) return 'secondary'
  if (props.spread > 0) return 'success'
  if (props.spread < 0) return 'danger'
  return 'secondary'
})

const buyDexUrl = computed(() => getDexUrl(props.buyPool.dexName, props.chain, props.buyPool.address))
const sellDexUrl = computed(() => getDexUrl(props.sellPool.dexName, props.chain, props.sellPool.address))

const onArbitrage = async () => {
  try {
    const result = await executeArbitrage(
      props.buyPool,
      props.sellPool,
      props.chain,
      props.amount,
      props.slippage,
    )
    emit('arbitrage-executed', result)
  } catch (e: unknown) {
    // Error already toasted inside useDexPoolSwap; no need to re-toast here
    console.error('Arbitrage failed:', e)
  }
}
</script>

<template>
  <Card pt:root:class="pool-pair-card" pt:body:class="p-3">
    <template #title>
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <Tag severity="success" class="text-xs">{{ buyPool.dexName }}</Tag>
          <i class="pi pi-arrow-right text-xs text-muted-color"></i>
          <Tag severity="danger" class="text-xs">{{ sellPool.dexName }}</Tag>
        </div>
        <!-- <Tag :severity="spreadSeverity" class="text-sm font-mono font-bold">
          <template v-if="spread !== null">
            {{ spread > 0 ? '+' : '' }}{{ spread.toFixed(2) }}%
          </template>
<template v-else>—</template>
</Tag> -->
      </div>
    </template>

    <template #content>
      <div class="grid grid-cols-1 md:grid-cols-11 gap-3 items-stretch">
        <!-- Пул покупки -->
        <div class="md:col-span-5 flex flex-col gap-2">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs font-semibold mark-buy">{{ $t('buy') }}</span>
            <a :href="buyDexUrl" target="_blank" class="text-xs pi pi-external-link hover:text-primary" />
          </div>

          <FloatLabel variant="on">
            <InputText :value="buyPool.dexName" readonly fluid class="text-sm" placeholder="" />
            <label class="text-xs">DEX</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText :value="buyPool.buyPrice !== null ? '$' + formatPrice(buyPool.buyPrice) : '—'" readonly fluid
              placeholder="" class="text-sm mark-buy" :class="{ 'opacity-50': !hasLiveBuy }" />
            <label class="text-xs">{{ $t('buyPrice') }}</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText :value="buyPool.sellPrice !== null ? '$' + formatPrice(buyPool.sellPrice) : '—'" readonly fluid
              placeholder="" class="text-sm mark-sell" :class="{ 'opacity-50': !hasLiveBuy }" />
            <label class="text-xs">{{ $t('sellPrice') }}</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText
              :value="buyPool.tvl !== null ? '$' + toK(buyPool.tvl) : (buyPool.liquidityUsd !== null ? '$' + toK(buyPool.liquidityUsd) : '—')"
              placeholder="" readonly fluid class="text-sm" :class="{ 'opacity-50': !hasLiveBuy }" />
            <label class="text-xs">{{ $t('liquidity') }}</label>
          </FloatLabel>

          <div class="text-xs text-muted-color font-mono mt-1">
            {{ shortAddress(buyPool.address) }}
          </div>

          <Button
            class="mt-2"
            severity="success"
            fluid
            :loading="loading"
            :disabled="loading || !hasLiveBuy || !hasLiveSell"
            @click="onArbitrage"
          >
            <i class="pi pi-bolt mr-2"></i>
            {{ $t('arbitrage') }}
          </Button>
        </div>

        <!-- Центр — спред -->
        <div class="md:col-span-1 flex flex-col items-center justify-center gap-1 py-2">
          <span class="text-xl font-mono font-bold" :class="{
            'mark-buy': (spread ?? 0) > 0,
            'mark-sell': (spread ?? 0) < 0,
            'text-muted-color': spread === null,
          }">
            <template v-if="spread !== null">
              {{ spread > 0 ? '+' : '' }}{{ spread.toFixed(2) }}%
            </template>
            <template v-else>—</template>
          </span>
          <!-- <i class="pi pi-arrow-right text-lg text-muted-color hidden md:block"></i> -->
          <!-- <i class="pi pi-arrow-down text-lg text-muted-color md:hidden"></i> -->
        </div>

        <!-- Пул продажи -->
        <div class="md:col-span-5 flex flex-col gap-2">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs font-semibold mark-sell">{{ $t('sell') }}</span>
            <a :href="sellDexUrl" target="_blank" class="text-xs pi pi-external-link hover:text-primary" />
          </div>

          <FloatLabel variant="on">
            <InputText :value="sellPool.dexName" readonly fluid class="text-sm" placeholder="" />
            <label class="text-xs">DEX</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText :value="sellPool.buyPrice !== null ? '$' + formatPrice(sellPool.buyPrice) : '—'" readonly fluid
              class="text-sm mark-buy" :class="{ 'opacity-50': !hasLiveSell }" placeholder="" />
            <label class="text-xs">{{ $t('buyPrice') }}</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText :value="sellPool.sellPrice !== null ? '$' + formatPrice(sellPool.sellPrice) : '—'" readonly fluid
              class="text-sm mark-sell" :class="{ 'opacity-50': !hasLiveSell }" placeholder="" />
            <label class="text-xs">{{ $t('sellPrice') }}</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText
              :value="sellPool.tvl !== null ? '$' + toK(sellPool.tvl) : (sellPool.liquidityUsd !== null ? '$' + toK(sellPool.liquidityUsd) : '—')"
              readonly fluid class="text-sm" :class="{ 'opacity-50': !hasLiveSell }" placeholder="" />
            <label class="text-xs">{{ $t('liquidity') }}</label>
          </FloatLabel>

          <div class="text-xs text-muted-color font-mono mt-1">
            {{ shortAddress(sellPool.address) }}
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped>
.pool-pair-card {
  /* border: 1px solid var(--p-content-border-color); */
  /* border-radius: var(--p-card-border-radius); */
  transition: box-shadow 0.2s ease;
}

.pool-pair-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.mark-buy {
  color: var(--p-green-500);
}

.mark-sell {
  color: var(--p-red-500);
}

:deep(.p-inputtext:read-only) {
  background-color: var(--p-content-background);
  cursor: default;
}
</style>
